import React from 'react';
import { ArrowLeft } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <a href="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </a>

        <h1 className="text-4xl font-bold mb-4">Terms of Use</h1>
        <p className="text-gray-400 mb-8">Last updated: March 16, 2026</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
          <p className="text-gray-300 leading-relaxed">
            By accessing or using Pulse Media ("the Service"), operated by Pulse Media ("we," "our," or "us"), you agree to be bound by these Terms of Use. If you do not agree to these terms, do not use the Service. We reserve the right to modify these terms at any time. Continued use of the Service after changes constitutes acceptance of the updated terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
          <p className="text-gray-300 leading-relaxed">
            Pulse Media is a streaming platform that provides access to movies, TV shows, and music videos. The Service is available via our website at pulseportal.tv and through our mobile application. Access to certain content requires a paid subscription or individual purchase.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. Account Registration</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            To access certain features of the Service, you must create an account. You agree to:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and update your account information</li>
            <li>Keep your password secure and confidential</li>
            <li>Accept responsibility for all activity under your account</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
          </ul>
          <p className="text-gray-300 leading-relaxed mt-4">
            You must be at least 13 years of age to create an account. If you are under 18, you must have parental or guardian consent to use the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. Subscriptions and Payments</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            Pulse Media offers a monthly subscription at $1.99/month. By subscribing, you agree to the following:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
            <li>Subscriptions are billed monthly on a recurring basis until cancelled</li>
            <li>You authorize us to charge your selected payment method each billing cycle</li>
            <li>Prices may change with advance notice; continued use after a price change constitutes acceptance</li>
            <li>Web payments are processed through Stripe; mobile payments through Google Play or Apple App Store</li>
            <li>You may cancel your subscription at any time; access continues through the end of the current billing period</li>
            <li>Refunds are handled in accordance with the refund policies of the applicable payment processor</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. Content and Intellectual Property</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            All content available on Pulse Media, including but not limited to movies, TV shows, music videos, images, text, graphics, logos, and software, is the property of Pulse Media or its content licensors and is protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Your subscription grants you a limited, non-exclusive, non-transferable, revocable license to access and stream content for personal, non-commercial use only. You may not copy, download, distribute, transmit, display, sell, license, or otherwise exploit any content without prior written permission.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. User Conduct</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            When using the Service, you agree not to:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
            <li>Use the Service for any unlawful purpose</li>
            <li>Share your account credentials with others</li>
            <li>Circumvent, disable, or interfere with security features of the Service</li>
            <li>Use any automated system to access the Service in a manner that sends more requests than a human could reasonably produce</li>
            <li>Upload, post, or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, or otherwise objectionable</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Record, capture, or redistribute any content from the platform</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. User-Generated Content</h2>
          <p className="text-gray-300 leading-relaxed">
            You may submit comments, ratings, and video reviews through the Service. By submitting content, you grant Pulse Media a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display that content in connection with the Service. You retain ownership of your submitted content but are responsible for ensuring it does not violate any third-party rights. We reserve the right to remove any user-generated content at our sole discretion.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Termination</h2>
          <p className="text-gray-300 leading-relaxed">
            We reserve the right to suspend or terminate your account at any time, with or without notice, for conduct that we determine violates these Terms of Use or is harmful to other users, us, or third parties. You may terminate your account at any time by requesting account deletion at pulseportal.tv/delete-account. Upon termination, your right to use the Service ceases immediately, and we may delete your account data in accordance with our Privacy Policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. Disclaimers</h2>
          <p className="text-gray-300 leading-relaxed">
            The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or secure. We make no warranties regarding the quality, accuracy, or availability of any content. Your use of the Service is at your sole risk.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
          <p className="text-gray-300 leading-relaxed">
            To the fullest extent permitted by law, Pulse Media shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill, arising out of or in connection with your use of the Service. Our total liability for any claim arising from these terms or the Service shall not exceed the amount you paid to us in the twelve months preceding the claim.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
          <p className="text-gray-300 leading-relaxed">
            These Terms of Use shall be governed by and construed in accordance with the laws of the United States. Any disputes arising from these terms or the Service shall be resolved through binding arbitration, except where prohibited by law.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
          <p className="text-gray-300 leading-relaxed">
            If you have any questions about these Terms of Use, please contact us at{' '}
            <a href="mailto:contact@pulsemedia.vip" className="text-cyan-400 hover:underline">contact@pulsemedia.vip</a>
            {' '}or visit our support page at{' '}
            <a href="/support" className="text-cyan-400 hover:underline">pulseportal.tv/support</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfUse;
